
import { KTSVG } from '../../../helpers'
import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { getUserByToken, login } from '../../../../app/modules/auth/core/_requests'
import { useAuth } from '../../../../app/modules/auth/core/Auth'
import { AxiosError } from 'axios';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
  user: Yup.string()
})

const initialValues = {
  email: '',
  password: '',
  user: 'student'
}

type Props = {
  regModal: Function,
  resetPasswordModal: Function
}

export function ActivityDrawer({ regModal, resetPasswordModal }: Props) {
  const [loading, setLoading] = useState(false)
  const { saveAuth, setCurrentUser } = useAuth()
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting, resetForm }) => {
      setLoading(true)
      try {
        const { data: auth } = await login(values.email, values.password, values.user)
        console.log("auth: " + JSON.stringify(auth))
        saveAuth(auth)
        const { data: user } = await getUserByToken(auth.api_token)
        console.log("user: " + JSON.stringify(user))
        setCurrentUser(user)

        setSubmitting(false)
        setLoading(false)

        resetForm()
        document.getElementById('kt_activities_close')?.click()

      } catch (e) {
        const err = e as AxiosError
        const error_data = err.response?.data
        const error_msg = error_data.message ?? 'Incorrect detail'

        saveAuth(undefined)
        setStatus(error_msg)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <div
      id='kt_activities'
      className='bg-body'
      data-kt-drawer='true'
      data-kt-drawer-name='activities'
      data-kt-drawer-activate='true'
      data-kt-drawer-overlay='true'
      data-kt-drawer-width="{default:'400px', 'lg': '500px', 'md': '300px', 'sm': '250px'}"
      data-kt-drawer-direction='end'
      data-kt-drawer-toggle='#kt_activities_toggle'
      data-kt-drawer-close='#kt_activities_close'
    >
      <div className='card shadow-none rounded-0 w-100'>
        <div className='card-header' id='kt_activities_header'>
          <h3 className='card-title fw-bolder text-dark'>Login</h3>

          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-active-light-primary me-n5'
              id='kt_activities_close'
            >
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
            </button>
          </div>
        </div>
        <div className='card-body position-relative' id='kt_activities_body'>
          <div
            id='kt_activities_scroll'
            className='position-relative scroll-y me-n5 pe-5'
            data-kt-scroll='true'
            data-kt-scroll-height='auto'
            data-kt-scroll-wrappers='#kt_activities_body'
            data-kt-scroll-dependencies='#kt_activities_header, #kt_activities_footer'
            data-kt-scroll-offset='5px'
          >
            <form
              className='form'
              onSubmit={formik.handleSubmit}
              noValidate
              id='kt_login_signin_form'
            >
              {/* begin::Heading */}
              <div className='text-center mb-10'>
                <h1 className='text-dark mb-3'>เข้าใช้งานระบบ</h1>
                <div className='text-gray-400 fw-bold fs-4'>
                  ยังไม่มี account?{' '}
                  <a
                    href='#'
                    className='text-primary'
                    onClick={() => {
                      regModal(true)
                    }}
                    id='kt_activities_close'
                  >
                    ลงทะเบียนเช้าใช้งาน
                  </a>
                </div>
              </div>
              {/* begin::Heading */}

              {formik.status ? (
                <div className='mb-lg-15 alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
              ) : (
                <div className='mb-10 bg-light-info p-8 rounded'>
                  <div className='text-info'>
                    กรุณาใช้อีเมลมหาวิทยาลัยที่ลงท้ายด้วย <strong>tu.ac.th</strong> เพื่อเข้าใช้งานระบบ
                  </div>
                </div>
              )}

              {/* begin::Form group */}
              <div className='fv-row mb-10'>
                <label className='form-label fs-6 fw-bolder text-dark'>อีเมล</label>
                <input
                  placeholder=''
                  {...formik.getFieldProps('email')}
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    { 'is-invalid': formik.touched.email && formik.errors.email },
                    {
                      'is-valid': formik.touched.email && !formik.errors.email,
                    }
                  )}
                  type='email'
                  name='email'
                  autoComplete='off'
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.email}</span>
                  </div>
                )}
              </div>
              {/* end::Form group */}

              {/* begin::Form group */}
              <div className='fv-row mb-10'>
                <div className='d-flex justify-content-between mt-n5'>
                  <div className='d-flex flex-stack mb-2'>
                    <label className='form-label fw-bolder text-dark fs-6 mb-0'>รหัสผ่าน</label>
                    <a
                      href='#'
                      className='text-primary ms-5'
                      onClick={() => {
                        resetPasswordModal(true)
                      }}
                      id='kt_activities_close'
                    >
                      ลืมรหัสผ่าน? (สำหรับนักศึกษาเท่านั้น)
                    </a>
                  </div>
                </div>
                <input
                  type='password'
                  autoComplete='off'
                  {...formik.getFieldProps('password')}
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    {
                      'is-invalid': formik.touched.password && formik.errors.password,
                    },
                    {
                      'is-valid': formik.touched.password && !formik.errors.password,
                    }
                  )}
                />
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.password}</span>
                    </div>
                  </div>
                )}
              </div>
              {/* end::Form group */}

              {/* begin::Form group */}
              <div className='fv-row mb-10'>
                <label className='form-label fs-6 fw-bolder text-dark'>ผู้ใช้งาน</label>
                <select
                  className='form-select form-select-solid form-select-lg'
                  {...formik.getFieldProps('user')}
                >
                  <option value='student'>นักศึกษา</option>
                  <option value='staff'>พนักงาน</option>
                </select>
                {formik.touched.user && formik.errors.user && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.user}</div>
                  </div>
                )}
                <a href="/media/pdfs/student_manual.pdf" target='_blank' className='mt-2'>คู่มือใช้งาน</a>
              </div>
              {/* end::Form group */}

              {/* begin::Action */}
              <div className='text-center'>
                <button
                  type='submit'
                  id='kt_sign_in_submit'
                  className='btn btn-lg btn-primary w-100 mb-5'
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {!loading && <span className='indicator-label'>เข้าใช้งาน</span>}
                  {loading && (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>

              </div>
              {/* end::Action */}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
